import { useState } from 'react';
import { AlertTriangle, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { startGoogleOAuth } from '@/components/GoogleAuthButton';

const inp = 'w-full rounded-xl border border-border bg-surface-input px-4 py-2.5 text-white placeholder:text-muted-foreground focus:border-destructive/60 focus:outline-none';
export const isDeletionBlocking = (d) => ['PENDING', 'PURGING', 'FAILED'].includes(d?.status);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'long' }) : '—');

export function PendingDeletionNotice() {
  const { profile, refreshProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try { await api.post('/account/deletion/cancel'); toast.success('Suppression annulée. Bon retour !'); await refreshProfile(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-5" data-testid="deletion-pending-notice">
      <p className="flex items-center gap-2 font-display font-semibold text-white"><AlertTriangle className="h-5 w-5 text-destructive" /> Suppression programmée</p>
      <p className="mt-2 text-sm text-white/80">
        Ton compte sera définitivement supprimé le <strong data-testid="deletion-scheduled-date">{fmtDate(profile?.deletion?.scheduled_for)}</strong>.
        D'ici là, l'accès au service est suspendu. Tu peux annuler à tout moment avant cette date.
      </p>
      <button onClick={cancel} disabled={busy} data-testid="deletion-cancel-button"
              className="mt-4 flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-black btn-glow disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Annuler la suppression
      </button>
    </div>
  );
}

const REAUTH_WINDOW_S = 14 * 60;

function lastAuthAgeSeconds(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const ts = (payload.amr || []).map((a) => a.timestamp).filter(Number.isFinite);
    return ts.length ? Date.now() / 1000 - Math.max(...ts) : Infinity;
  } catch { return Infinity; }
}

function Consequences() {
  return (
    <ul className="ml-5 list-disc space-y-1 text-sm text-white/80 marker:text-destructive" data-testid="deletion-consequences">
      <li>Le compte est placé en suppression programmée pendant <strong>30 jours</strong> ; l'accès ordinaire est suspendu.</li>
      <li>Tu peux te reconnecter pendant ce délai pour annuler la demande.</li>
      <li>Ensuite, suppression définitive et automatique : musiques personnelles, fichiers audio, participations aux challenges et crédits inutilisés.</li>
      <li>Seules les informations exigées par la loi (ex. références de commandes payées) sont conservées. Aucune récupération possible après suppression définitive.</li>
    </ul>
  );
}

function RequestForm() {
  const { session, refreshProfile } = useAuth();
  const providers = session?.user?.app_metadata?.providers || [];
  const hasPassword = providers.includes('email');
  const googleVerified = !hasPassword && lastAuthAgeSeconds(session?.access_token || '') < REAUTH_WINDOW_S;
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (hasPassword) {
        const { error } = await supabase.auth.signInWithPassword({ email: session.user.email, password });
        if (error) { toast.error('Mot de passe incorrect.'); return; }
      }
      await api.post('/account/deletion', { confirmation: confirm, source: 'web' });
      toast.success('Suppression programmée : tu as 30 jours pour changer d\'avis.');
      await refreshProfile();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  const canConfirm = hasPassword || googleVerified;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="deletion-request-form">
      <Consequences />
      {hasPassword && (
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe actuel (vérification d'identité)" className={inp} data-testid="deletion-password" />
      )}
      {!hasPassword && !googleVerified && (
        <div className="space-y-2" data-testid="deletion-google-step">
          <p className="text-xs text-muted-foreground">Compte Google : confirme d'abord ton identité par une nouvelle connexion Google.</p>
          <button type="button" onClick={() => startGoogleOAuth(window.location.pathname, { reauth: true })} data-testid="deletion-reauth-google"
                  className="rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-[#1f1f1f] hover:bg-[#f8f9fa]">
            Confirmer mon identité avec Google
          </button>
        </div>
      )}
      {!hasPassword && googleVerified && <p className="text-xs text-[#22C55E]" data-testid="deletion-google-verified">Identité Google confirmée.</p>}
      {canConfirm && (
        <>
          <input required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Tape SUPPRIMER pour confirmer" className={inp} data-testid="deletion-confirm-input" />
          <button type="submit" disabled={busy || confirm.trim().toUpperCase() !== 'SUPPRIMER'} data-testid="deletion-submit"
                  className="flex items-center gap-2 rounded-full bg-destructive px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Programmer la suppression (30 jours)
          </button>
        </>
      )}
    </form>
  );
}

export function DeletionPanel() {
  const { profile } = useAuth();
  return isDeletionBlocking(profile?.deletion) ? <PendingDeletionNotice /> : <RequestForm />;
}
